/**
 * The human check's React half (ADR-123; the rules are in humanCheck.ts).
 *
 * `useHumanCheck` owns one thing: a pass for this tenant. `obtain()` resolves
 * to one — the stored pass while it is good, otherwise a fresh challenge in a
 * frame the panel draws (`frame`), exchanged at POST /chat/human-check. Two
 * callers asking at once share the same challenge. The frame is 1px and
 * transparent until Turnstile asks the visitor to interact, when it shows as
 * a small card above the composer — only while the panel is open — and it is
 * removed as soon as a token has been exchanged. The caller renders `frame`
 * in ONE place whether the panel is open or not: a frame that changes parent
 * is remounted, and a remounted frame starts the challenge again.
 *
 * A check that cannot run on this page (the frame blocked, the page an opaque
 * origin, the widget misconfigured) is remembered: in `log` it is not tried
 * again on every send, so a site whose CSP blocks the frame is told once.
 *
 * It fails loudly rather than waiting forever, with the reason in the console
 * for whoever runs the site: no frame within 10 s is a CSP that will not frame
 * the challenge page, no challenge script within 15 s after that is Cloudflare
 * unreachable, and a visitor who has not finished an interactive challenge in
 * two minutes has gone.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import {
  challengeUrl,
  clearPass,
  exchangeToken,
  frameOrigin,
  isTerminalFrameError,
  isUsablePass,
  loadPass,
  parseFrameMessage,
  parseHumanCheck,
  savePass,
  type HumanCheckConfig,
  type HumanCheckMode,
  type StoredPass,
} from './humanCheck';

const READY_TIMEOUT_MS = 10_000;
const RENDER_TIMEOUT_MS = 15_000;
const SOLVE_TIMEOUT_MS = 120_000;
/** Turnstile's `flexible` widget: 65px, plus room for its focus ring. */
const INTERACTIVE_HEIGHT = 72;

/** Why a check did not produce a pass. The visitor sees one sentence for all of them. */
export class HumanCheckError extends Error {
  constructor(readonly code: string) {
    super(`human check: ${code}`);
    this.name = 'HumanCheckError';
  }
}

export interface HumanCheck {
  /** The mode the server last said, or null when there is no check to run. */
  mode(): HumanCheckMode | null;
  /** A pass that is good right now, without waiting. */
  current(): string | null;
  /** A pass, running the challenge when needed. Rejects with a HumanCheckError. */
  obtain(opts?: { fresh?: boolean }): Promise<string>;
  /** A refusal carried the check's configuration: adopt it. */
  learn(raw: unknown): void;
  /** The server no longer accepts the stored pass. */
  invalidate(): void;
}

interface Pending {
  promise: Promise<string>;
  resolve: (pass: string) => void;
  reject: (err: HumanCheckError) => void;
}

interface Active {
  id: number;
  url: string;
  origin: string;
}

export function useHumanCheck(opts: {
  apiUrl: string;
  tenantId: string;
  config: HumanCheckConfig | null;
  language: string;
  persist: boolean;
  title: string;
  /** Whether an interactive challenge may be shown (the panel is open). */
  visible: boolean;
}): { check: HumanCheck; frame: ReactNode } {
  const { apiUrl, tenantId, language, persist, title, visible } = opts;

  const configRef = useRef<HumanCheckConfig | null>(opts.config);
  // Read once, not on every render (a streaming reply renders per delta).
  const [initialPass] = useState<StoredPass | null>(() => (persist ? loadPass(tenantId) : null));
  const passRef = useRef<StoredPass | null>(initialPass);
  const brokenRef = useRef('');
  const pendingRef = useRef<Pending | null>(null);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const idRef = useRef(0);
  const lastErrorRef = useRef('');

  const [active, setActive] = useState<Active | null>(null);
  const [interactive, setInteractive] = useState(false);
  const activeRef = useRef<Active | null>(null);
  activeRef.current = active;

  useEffect(() => {
    if (opts.config) configRef.current = opts.config;
  }, [opts.config]);

  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };

  const settle = useCallback((outcome: { pass: string } | { error: HumanCheckError }) => {
    clearTimers();
    setActive(null);
    setInteractive(false);
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (!pending) return;
    if ('pass' in outcome) pending.resolve(outcome.pass);
    else pending.reject(outcome.error);
  }, []);

  const fail = useCallback(
    (code: string) => {
      const config = configRef.current;
      const origin = config ? frameOrigin(config) : 'the challenge page';
      if (code === 'frame_unavailable' || code === 'opaque_origin' || isTerminalFrameError(code)) brokenRef.current = code;
      if (code === 'opaque_origin') {
        console.error(
          '[PweChat] The security check cannot run on this page: it has no origin of its own (a sandboxed or srcdoc frame), ' +
            'so the check has nowhere to send its answer. Load the widget on a page served from a website.',
        );
      } else if (code === 'frame_unavailable') {
        console.error(
          `[PweChat] The security check at ${origin} did not load. If this site sends a Content-Security-Policy, ` +
            `add ${origin} to frame-src (it is already needed in connect-src for streaming).`,
        );
      } else if (isTerminalFrameError(code) && code !== 'unsupported') {
        console.error(`[PweChat] The security check is misconfigured (${code}). This is the Ema Answers service, not this site.`);
      } else {
        console.warn(`[PweChat] The security check did not complete (${code}).`);
      }
      settle({ error: new HumanCheckError(code) });
    },
    [settle],
  );

  const start = useCallback(() => {
    const config = configRef.current;
    if (!config) return;
    if (!window.location.origin || window.location.origin === 'null') {
      fail('opaque_origin');
      return;
    }
    const origin = frameOrigin(config);
    const theme =
      typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
    idRef.current += 1;
    lastErrorRef.current = '';
    setInteractive(false);
    setActive({
      id: idRef.current,
      origin,
      url: challengeUrl(config, { origin: window.location.origin, language, theme }),
    });
    clearTimers();
    timersRef.current.push(setTimeout(() => fail('frame_unavailable'), READY_TIMEOUT_MS));
  }, [fail, language]);

  // One listener for the frame's messages, checked against the frame this
  // widget mounted and the origin it was mounted from: any other window on
  // the page can post anything.
  useEffect(() => {
    if (!active) return;
    const onMessage = (event: MessageEvent) => {
      const current = activeRef.current;
      if (!current || event.origin !== current.origin) return;
      if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) return;
      const message = parseFrameMessage(event.data);
      if (!message) return;
      switch (message.type) {
        case 'ready':
          clearTimers();
          timersRef.current.push(setTimeout(() => fail('challenge_unavailable'), RENDER_TIMEOUT_MS));
          return;
        case 'rendered':
          clearTimers();
          timersRef.current.push(setTimeout(() => fail(lastErrorRef.current || 'timeout'), SOLVE_TIMEOUT_MS));
          return;
        case 'interactive':
          setInteractive(true);
          return;
        case 'settled':
          setInteractive(false);
          return;
        case 'expired':
          return;
        case 'error':
          lastErrorRef.current = message.code || 'error';
          if (isTerminalFrameError(message.code)) fail(message.code);
          return;
        case 'token': {
          const id = current.id;
          clearTimers();
          exchangeToken(apiUrl, tenantId, message.token).then((outcome) => {
            if (activeRef.current?.id !== id) return;
            if (outcome.kind === 'ok') {
              passRef.current = outcome.pass;
              if (persist) savePass(tenantId, outcome.pass);
              settle({ pass: outcome.pass.pass });
              return;
            }
            fail(outcome.kind === 'failed' ? 'human_check_failed' : outcome.code);
          });
          return;
        }
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [active, apiUrl, tenantId, persist, fail, settle]);

  // Unmounted mid-check: nobody is left to answer.
  useEffect(
    () => () => {
      clearTimers();
      pendingRef.current?.reject(new HumanCheckError('unmounted'));
      pendingRef.current = null;
    },
    [],
  );

  const check = useMemo<HumanCheck>(
    () => ({
      mode: () => configRef.current?.mode ?? null,
      current: () => (isUsablePass(passRef.current) ? passRef.current.pass : null),
      obtain: ({ fresh = false } = {}) => {
        if (!fresh && isUsablePass(passRef.current)) return Promise.resolve(passRef.current.pass);
        if (pendingRef.current) return pendingRef.current.promise;
        if (!configRef.current) return Promise.reject(new HumanCheckError('not_configured'));
        // In `log` nothing waits for the check, so a check that cannot run here
        // is not started (and reported) again on every send. Under `enforce` a
        // send is a visitor asking to try again, and it tries.
        if (brokenRef.current && configRef.current.mode === 'log') {
          return Promise.reject(new HumanCheckError(brokenRef.current));
        }
        let resolve!: (pass: string) => void;
        let reject!: (err: HumanCheckError) => void;
        const promise = new Promise<string>((res, rej) => {
          resolve = res;
          reject = rej;
        });
        pendingRef.current = { promise, resolve, reject };
        start();
        return promise;
      },
      learn: (raw: unknown) => {
        const parsed = parseHumanCheck(raw);
        if (parsed) configRef.current = parsed;
      },
      invalidate: () => {
        passRef.current = null;
        if (persist) clearPass(tenantId);
      },
    }),
    [start, persist, tenantId],
  );

  // Hidden, the frame is 1px and transparent but NOT clipped or display:none:
  // a browser may stop running a cross-origin frame it considers invisible,
  // and the challenge has to run to produce a token nobody sees. Shown, it is
  // a card fixed above the composer, wherever the panel sits.
  const shown = interactive && visible;
  const style: CSSProperties = shown
    ? { display: 'block', width: '100%', height: `${INTERACTIVE_HEIGHT}px`, border: 0, colorScheme: 'normal' }
    : {
        position: 'fixed',
        left: 0,
        bottom: 0,
        width: '1px',
        height: '1px',
        border: 0,
        opacity: 0,
        pointerEvents: 'none',
        colorScheme: 'normal',
      };
  const cardStyle: CSSProperties = {
    position: 'fixed',
    left: '50%',
    bottom: '96px',
    transform: 'translateX(-50%)',
    width: 'min(320px, calc(100vw - 32px))',
    padding: '8px',
    background: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 8px 30px rgba(0,0,0,0.18)',
    zIndex: 2147483000,
    pointerEvents: 'auto',
  };

  const frame = active ? (
    <div style={shown ? cardStyle : undefined} data-pwe="human-check-card">
      <iframe
        key={active.id}
        ref={iframeRef}
        src={active.url}
        title={title}
        style={style}
        data-pwe="human-check"
        aria-hidden={shown ? undefined : true}
        tabIndex={shown ? 0 : -1}
      />
    </div>
  ) : null;

  return { check, frame };
}
